import styled from "styled-components";
import { pxToRem } from "../../styles/utils";

export const ProfilePageContainer = styled.div`
  display: flex;
  height: calc(100% - ${pxToRem(24)});
  margin: ${pxToRem(12)};
`;

export const ContentWrapper = styled.div`
  min-width: 0;
  flex: 1;
  overflow: hidden;
  scrollbar-width: none;
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  box-shadow: ${({ theme }) => theme.shadows.md}, ${({ theme }) => theme.shadows.ring};

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const ContentInner = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  max-width: ${pxToRem(1480)};
  box-sizing: border-box;
  padding: ${pxToRem(28)} ${pxToRem(32)} ${pxToRem(40)};

  @media (max-width: 760px) {
    padding: ${pxToRem(20)} ${pxToRem(16)} ${pxToRem(32)};
  }
`;

export const Title = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${pxToRem(26)};
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.02em;
`;

export const TableHeaderBar = styled.div`
  display: flex;
  align-items: center;
  margin: ${pxToRem(26)} 0 ${pxToRem(18)};
`;

export const SectionTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${pxToRem(18)};
  font-weight: 600;
`;

export const PricingScroll = styled.div`
  width: 100%;
  min-height: 0;
  flex: 1 1 auto;
  overflow: auto;
  padding-bottom: ${pxToRem(4)};
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const PricingTable = styled.table`
  width: 100%;
  min-width: ${pxToRem(760)};
  min-height: clamp(${pxToRem(560)}, calc(100vh - ${pxToRem(300)}), ${pxToRem(740)});
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;

  thead {
    position: sticky;
    top: 0;
    z-index: 3;
    background: ${({ theme }) => theme.colors.white};
  }

  th:first-child,
  td:first-child {
    width: 18%;
  }

  @media (max-width: 760px) {
    min-height: ${pxToRem(520)};
  }
`;

export const EmptyHeaderCell = styled.th`
  padding: 0 ${pxToRem(16)} ${pxToRem(24)};
  border-bottom: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
`;

export const PlanHeader = styled.th<{ $current: boolean }>`
  position: relative;
  overflow: hidden;
  padding: 0 ${pxToRem(18)} ${pxToRem(24)};
  border-bottom: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ $current }) => ($current ? `${pxToRem(10)} ${pxToRem(10)} 0 0` : 0)};
  background: ${({ $current, theme }) => ($current ? theme.colors.brandSubtle : "transparent")};
  text-align: center;
  vertical-align: top;
`;

export const PlanStatusSpace = styled.div`
  min-height: ${pxToRem(48)};
  margin-bottom: ${pxToRem(10)};
`;

export const PlanBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${pxToRem(8)};
  margin: 0 ${pxToRem(-18)};
  padding: ${pxToRem(8)} ${pxToRem(15)};
  background: ${({ theme }) => theme.colors.brandMedium};
  color: ${({ theme }) => theme.colors.brandDark};
  font-size: ${pxToRem(12)};
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
`;

export const PlanBadgeUntil = styled.span<{ $warning: boolean }>`
  color: ${({ $warning }) => ($warning ? "#c2861f" : "inherit")};
  font-weight: 400;
`;

export const PlanName = styled.div`
  margin-bottom: ${pxToRem(7)};
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${pxToRem(18)};
  font-weight: 600;
`;

export const PlanPrice = styled.div`
  min-height: ${pxToRem(36)};
  margin-bottom: ${pxToRem(14)};
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${pxToRem(26)};
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.02em;

  span {
    color: ${({ theme }) => theme.colors.inkMuted};
    font-size: ${pxToRem(13)};
    font-weight: 400;
    letter-spacing: 0;
  }
`;

export const PlanDescription = styled.div`
  height: ${pxToRem(56)};
  margin-bottom: ${pxToRem(18)};
  overflow: hidden;
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${pxToRem(12)};
  font-weight: 400;
  line-height: ${pxToRem(19)};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;

  @media (max-width: 760px) {
    display: none;
    height: 0;
    margin-bottom: ${pxToRem(12)};
  }
`;

export const PlanAction = styled.div`
  > button {
    width: 100%;
    min-width: 0;
    max-width: none;
    height: ${pxToRem(46)};
    padding: 0 ${pxToRem(14)};
    font-size: ${pxToRem(13)};
    font-weight: 500;
  }
`;

export const FeatureRow = styled.tr`
  &:last-child td {
    border-bottom: 0;
  }
`;

export const FeatureNameCell = styled.td`
  padding: ${pxToRem(16)} ${pxToRem(16)};
  border-bottom: ${pxToRem(1)} solid ${({ theme }) => theme.borders.divider};
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${pxToRem(14)};
  font-weight: 400;
  vertical-align: middle;
`;

export const FeatureCell = styled.td<{ $current: boolean; $lastRow: boolean }>`
  padding: ${pxToRem(16)} ${pxToRem(16)};
  border-bottom: ${({ $lastRow, theme }) =>
    $lastRow ? 0 : `${pxToRem(1)} solid ${theme.borders.divider}`};
  border-radius: ${({ $current, $lastRow }) =>
    $current && $lastRow ? `0 0 ${pxToRem(10)} ${pxToRem(10)}` : 0};
  background: ${({ $current, theme }) => ($current ? theme.colors.brandSubtle : "transparent")};
  text-align: center;
  vertical-align: middle;
`;

export const FeatureYes = styled.span`
  display: inline-flex;
  color: ${({ theme }) => theme.colors.success};

  svg {
    width: ${pxToRem(22)};
    height: ${pxToRem(22)};
  }
`;

export const FeatureNo = styled.span`
  color: rgba(31, 10, 53, 0.22);
  font-size: ${pxToRem(16)};
`;

export const FootNote = styled.p`
  margin: ${pxToRem(18)} 0 0;
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${pxToRem(12)};
  line-height: 1.45;
`;

export const LoadingState = styled.div`
  padding: ${pxToRem(32)} 0;
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${pxToRem(14)};
`;

export const ErrorState = styled.div`
  display: flex;
  align-items: center;
  gap: ${pxToRem(12)};
  padding: ${pxToRem(28)} 0;
  color: ${({ theme }) => theme.colors.danger};
  font-size: ${pxToRem(14)};

  > button {
    width: auto;
    min-width: ${pxToRem(110)};
    height: ${pxToRem(42)};
    padding: 0 ${pxToRem(14)};
    font-size: ${pxToRem(13)};
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${pxToRem(20)};
  background: rgba(0, 0, 0, 0.5);
`;

export const ModalCard = styled.div`
  width: 100%;
  max-width: ${pxToRem(460)};
  padding: ${pxToRem(22)};
  border-radius: ${pxToRem(16)};
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0 ${pxToRem(18)} ${pxToRem(48)} rgba(31, 10, 53, 0.24);
`;

export const ModalTitle = styled.h3`
  margin: 0 0 ${pxToRem(8)};
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${pxToRem(18)};
  font-weight: 600;
`;

export const ModalText = styled.p`
  margin: 0 0 ${pxToRem(14)};
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${pxToRem(13)};
  line-height: 1.45;
`;

export const ModalSummary = styled.div`
  margin-bottom: ${pxToRem(14)};
  padding: ${pxToRem(10)} ${pxToRem(12)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.divider};
  border-radius: ${pxToRem(10)};
  font-size: ${pxToRem(13)};

  > div {
    display: flex;
    justify-content: space-between;
    gap: ${pxToRem(12)};
    padding: ${pxToRem(4)} 0;
  }

  span {
    color: ${({ theme }) => theme.colors.inkSoft};
  }

  strong {
    color: ${({ theme }) => theme.colors.ink};
    font-weight: 600;
    text-align: right;
  }
`;

export const ModalSetting = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${pxToRem(16)};
  margin-bottom: ${pxToRem(12)};
  padding: ${pxToRem(13)} 0;
  border-top: ${pxToRem(1)} solid ${({ theme }) => theme.borders.divider};
  border-bottom: ${pxToRem(1)} solid ${({ theme }) => theme.borders.divider};
`;

export const SettingName = styled.div`
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${pxToRem(14)};
  font-weight: 500;
`;

export const SettingNote = styled.div`
  margin-top: ${pxToRem(3)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${pxToRem(12)};
  line-height: 1.4;
`;

export const AgreementText = styled.div`
  margin-bottom: ${pxToRem(16)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${pxToRem(11)};
  line-height: 1.45;

  a {
    color: ${({ theme }) => theme.colors.brand};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export const ModalActions = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-end;
  gap: ${pxToRem(8)};

  > button {
    width: auto;
    min-width: 0;
    height: ${pxToRem(42)};
    padding: 0 ${pxToRem(14)};
    font-size: ${pxToRem(13)};
    white-space: nowrap;
  }
`;

export const ModalActionsSpacer = styled.span`
  min-width: ${pxToRem(8)};
  flex: 1 1 auto;
`;
